/**
 * Dashboard Sidebar Component
 *
 * Simple floating left sidebar for product navigation (diwoCatalyst style).
 *
 * Features:
 * - Fixed positioned white box with shadow (light mode)
 * - Click sidebar to toggle expand/collapse
 * - Collapsed: Icon-only (80px width)
 * - Expanded: Icon + label (256px width)
 * - Blue accent for active product
 * - Product switching with full page reload
 *
 * Author: Dine AI
 * Created: November 21, 2025
 * Updated: December 16, 2025 - Light mode redesign
 */

import React, { useState } from 'react';
import Cookies from 'js-cookie';
import { Bot, Phone } from 'lucide-react';

interface DashboardSidebarProps {
  activeProduct: 'virtual_assistant' | 'virtual_host';
  virtualAssistantAvailable: boolean;
  virtualHostAvailable: boolean;
  showSidebar: boolean;
  children: (sidebarExpanded: boolean) => React.ReactNode;
}

const DashboardSidebar: React.FC<DashboardSidebarProps> = ({
  activeProduct,
  virtualAssistantAvailable,
  virtualHostAvailable,
  showSidebar,
  children
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [switching, setSwitching] = useState(false);

  /**
   * Switch to different product dashboard
   */
  const handleSwitchProduct = async (productId: string) => {
    // Don't switch if already active
    if (productId === activeProduct) {
      return;
    }

    // Check if product is available
    if (productId === 'virtual_assistant' && !virtualAssistantAvailable) {
      alert('Virtual Assistant is not available on your subscription.\n\nPlease upgrade to access this feature.');
      return;
    }
    if (productId === 'virtual_host' && !virtualHostAvailable) {
      alert('Virtual Host is not available on your subscription.\n\nPlease upgrade to access this feature.');
      return;
    }

    try {
      setSwitching(true);

      // Call backend to switch active product
      const response = await fetch('/api/dashboard/switch-product', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${Cookies.get('auth_token')}`
        },
        body: JSON.stringify({
          product: productId
        })
      });

      if (!response.ok) {
        throw new Error('Failed to switch product');
      }

      // Full page reload to re-initialize with new product
      window.location.href = '/restaurant-dashboard';

    } catch (error) {
      console.error('Error switching product:', error);
      alert('Failed to switch product. Please try again.');
      setSwitching(false);
    }
  };

  const menuItems = [
    {
      id: 'virtual_assistant',
      label: 'Virtual Assistant',
      icon: Bot,
      available: virtualAssistantAvailable
    },
    {
      id: 'virtual_host',
      label: 'Virtual Host',
      icon: Phone,
      available: virtualHostAvailable
    }
  ];

  // Don't render sidebar if not showing
  if (!showSidebar) {
    return <>{children(false)}</>;
  }

  return (
    <>
      {/* Floating Sidebar - No background when collapsed, white bg when expanded */}
      <div
        onClick={() => setIsExpanded(!isExpanded)}
        className={`fixed top-24 z-50 flex flex-col transition-all duration-300 ease-in-out cursor-pointer ${
          isExpanded
            ? 'w-64 left-8 bg-white shadow-sm border border-gray-200 rounded-2xl overflow-hidden'
            : 'w-20 left-6'
        }`}
        style={{ maxHeight: 'calc(100vh - 8rem)' }}
      >
        {/* Navigation */}
        <nav className="flex-1 py-4 overflow-y-auto">
          {/* Section Label */}
          {isExpanded && (
            <div className="mb-3 px-5">
              <span className="text-xs font-semibold text-gray-400 uppercase tracking-wider">PRODUCTS</span>
            </div>
          )}

          {/* Menu Items */}
          <div className={`space-y-1.5 ${isExpanded ? 'px-3' : 'px-2'}`}>
            {menuItems.map((item) => {
              const Icon = item.icon;
              const isActive = activeProduct === item.id;
              const isAvailable = item.available;

              if (!isExpanded) {
                // Collapsed: Icon only in rounded box
                return (
                  <div
                    key={item.id}
                    onClick={(e) => {
                      e.stopPropagation();
                      if (isAvailable) {
                        handleSwitchProduct(item.id);
                      }
                    }}
                    className="flex justify-center cursor-pointer"
                  >
                    <div className={`w-14 h-14 flex items-center justify-center rounded-xl transition-all ${
                      isActive && isAvailable
                        ? 'bg-[#FDB913] shadow-md shadow-yellow-200'
                        : isAvailable
                        ? 'bg-gray-100 hover:bg-gray-200'
                        : 'bg-gray-50 opacity-40 cursor-not-allowed'
                    }`}>
                      <Icon size={20} className={isActive && isAvailable ? 'text-black' : 'text-gray-600'} />
                    </div>
                  </div>
                );
              }

              // Expanded: Icon + Text
              return (
                <div
                  key={item.id}
                  onClick={(e) => {
                    e.stopPropagation();
                    if (isAvailable) {
                      handleSwitchProduct(item.id);
                    }
                  }}
                  className={`flex items-center space-x-3 px-3 py-2.5 rounded-xl transition-all cursor-pointer ${
                    isActive && isAvailable
                      ? 'bg-yellow-50'
                      : isAvailable
                      ? 'hover:bg-gray-50'
                      : 'opacity-40 cursor-not-allowed'
                  }`}
                >
                  <div className={`w-12 h-12 flex items-center justify-center rounded-xl ${
                    isActive && isAvailable
                      ? 'bg-[#FDB913] shadow-sm shadow-yellow-200'
                      : 'bg-gray-100'
                  }`}>
                    <Icon size={20} className={isActive && isAvailable ? 'text-black' : 'text-gray-600'} />
                  </div>
                  <span className={`text-sm font-medium ${
                    isActive && isAvailable ? 'text-gray-900' : 'text-gray-700'
                  }`}>
                    {item.label}
                  </span>
                </div>
              );
            })}
          </div>
        </nav>
      </div>

      {/* Main Content with dynamic margin */}
      <div className={`transition-all duration-300 ease-in-out ${
        isExpanded ? 'ml-72' : 'ml-28'
      }`}>
        {children(isExpanded)}
      </div>

      {/* Loading Overlay */}
      {switching && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-8 shadow-2xl">
            <div className="flex items-center gap-4">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
              <p className="text-gray-700 font-medium">Switching products...</p>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default DashboardSidebar;
